const TURQUOISE = '#0DBEBB';
const TURQUOISE_DARK = '#088482';
const GREEN_DARK = '#237438';
const LIGHT_GRAY_LIGHTEST = '#F4F4F4';
const LIGHT_GRAY_LIGHTER = '#EAEAEA';
const LIGHT_GRAY = '#CECECE';
const DARK_GRAY_LIGHTER = '#878787';
const DARK_GRAY_DARKEST = '#0F0F0F';
const YELLOW = '#F9C327';
const YELLOW_DARKER = '#3E3100';
const RED_DARK = '#A02015';
const WHITE = '#FFFFFF';

// Colors
export const COLOR = {
  /* TURQUOISE */
  'turquoise-lighter': '#E6FCFC',
  'turquoise-light': '#A9F6F5',
  turquoise: TURQUOISE,
  'turquoise-dark': TURQUOISE_DARK,
  'turquoise-darker': '#081E1C',

  /* GREEN */
  'green-lighter': '#DBFCDB',
  'green-light': '#8FD88F',
  green: '#34993C',
  'green-dark': GREEN_DARK,
  'green-darker': '#002300',

  /* YELLOW */
  'yellow-lighter': '#F9E9B7',
  'yellow-light': '#FFD671',
  yellow: '#F9C327',
  'yellow-dark': '#B8921A',
  'yellow-darker': '#3E3100',

  /* ORANGE */
  'orange-lighter': '#FFD6BB',
  'orange-light': '#FC9F60',
  orange: '#ED6B00',
  'orange-dark': '#AF5016',
  'orange-darker': '#38180C',

  /* RED */
  'red-lighter': '#FBE6E6',
  'red-light': '#F19794',
  'red-medium-light': '#DF3020',
  red: '#CA2B1D',
  'red-dark': '#A02015',
  'red-darker': '#410704',
  'red-darkest': '#694542',

  /* PURPLE */
  'purple-lighter': '#EBDEFC',
  'purple-light': '#C285F9',
  purple: '#8724B5',
  'purple-dark': '#542368',
  'purple-darker': '#170021',

  /* BLUE */
  'blue-lighter': '#BBD4EF',
  'blue-light': '#668BCC',
  blue: '#24418B',
  'blue-dark': '#132760',
  'blue-darker': '#00002F',

  /* LIGHT GRAY */
  'light-gray-lightest': '#F4F4F4',
  'light-gray-lighter': '#EAEAEA',
  'light-gray-light': '#E2E2E2',
  'light-gray': LIGHT_GRAY,
  'light-gray-dark': '#BFBFBF',
  'light-gray-darker': '#B2B2B2',
  'light-gray-darkest': '#A0A0A0',

  /* DARK GRAY */
  'dark-gray-lightest': '#999999',
  'dark-gray-lighter': DARK_GRAY_LIGHTER,
  'dark-gray-light': '#5D5D5D',
  'dark-gray': '#3F3F3F',
  'dark-gray-dark': '#2D2D2D',
  'dark-gray-darker': '#1E1E1E',
  'dark-gray-darkest': DARK_GRAY_DARKEST,

  /* BLACK / WHITE / TRANSPARENT */
  black: '#000000',
  white: WHITE,
  'white-unselected': '#F1F1F1',
  transparent: 'transparent',

  /* TEXT */
  text: DARK_GRAY_DARKEST,

  /* MAIN ABSTRACTED COLORS */
  primary: TURQUOISE_DARK,
  'primary-light': TURQUOISE,
  'primary-dark': '#045e5e',
  'primary-secondary': TURQUOISE_DARK,
  'primary-secondary-light': TURQUOISE_DARK,

  /* STATE COLOURS */
  success: GREEN_DARK,
  'success-text': WHITE,
  warning: YELLOW,
  'warning-text': YELLOW_DARKER,
  error: RED_DARK,
  'error-text': WHITE,

  /* MISC COLORS */
  'app-bar-active': TURQUOISE,
  'app-bar': '#3F3F3F',
  'modal-dark-info-box': '#666666',
  'modal-dark-info-box-error': '#FCCBCA',
  'modal-dark-inner-section': '#444444',
  'modal-overlay': 'rgba(0, 0, 0, 0.5)',
  modal: '#0DBDB9',
  'player-yellow': '#FCB130',
  'solink-logo-arc': '#00404c',

  textColor: DARK_GRAY_LIGHTER,
  lightGrayHighlight: LIGHT_GRAY_LIGHTEST,
  fieldBorderColor: LIGHT_GRAY,
  fieldSuccessShadow: `0 0 0 1px inset ${GREEN_DARK}`,
  fieldErrorShadow: `0 0 0 1px inset ${RED_DARK}`,
  fieldWarningShadow: `0 0 0 1px inset ${YELLOW}`,
  fieldFocusShadow: `0 0 0 1px inset ${TURQUOISE_DARK}`,
  fieldLabelColor: DARK_GRAY_LIGHTER,
  fieldDisabledBG: LIGHT_GRAY_LIGHTER,
  containerBackground: WHITE,
  'camera-card-highlight-background': '#505050',
  'button-dark-hover': '#004545',
};

// Sizes
export const SIZE = {
  '4': '4px',
  '8': '8px',
  '10': '10px',
  '12': '12px',
  '16': '16px',
  '20': '20px',
  '24': '24px',
  '28': '28px',
  '32': '32px',
  '36': '36px',
  '40': '40px',
  '48': '48px',
  '60': '60px',
};

// Font sizes
export const FONT_SIZE = {
  '10': '10px',
  '11': '11px',
  '12': '12px',
  '13': '13px',
  '14': '14px',
  '15': '15px',
  '16': '16px',
  '18': '18px',
  '20': '20px',
  '21': '21px',
  '22': '22px',
  '24': '24px',
  '27': '27px',
  '32': '32px',
  '36': '36px',
  '48': '48px',
};

export interface InputColors {
  background: string;
  backgroundDisabled: string;
  color: string;
  borderColor: string;
  borderColorDisabled: string;
  borderColorError: string;
}

export const INPUT_COLORS: { [key in ColorMode]: InputColors } = {
  light: {
    background: COLOR['white'],
    backgroundDisabled: COLOR['fieldDisabledBG'],
    color: COLOR['text'],
    borderColor: COLOR['fieldBorderColor'],
    borderColorDisabled: COLOR['fieldBorderColor'],
    borderColorError: COLOR['error'],
  },
  dark: {
    background: COLOR['dark-gray'],
    backgroundDisabled: COLOR['dark-gray-darker'],
    color: COLOR['white'],
    borderColor: 'transparent',
    borderColorDisabled: COLOR['dark-gray-darker'],
    borderColorError: COLOR['red'],
  },
  darker: {
    background: COLOR['dark-gray-dark'],
    backgroundDisabled: COLOR['dark-gray-darker'],
    color: COLOR['white'],
    borderColor: 'transparent',
    borderColorDisabled: COLOR['dark-gray-darker'],
    borderColorError: COLOR['red'],
  },
};

// Other
export const fieldLabelFontSize = '13px';
export const fieldLabelMargin = '0 0 6px';
export const fieldDisabledOpacity = 0.7;
export const fieldBorderRadius = '3px';
export const fieldFocusShadow = `0 0 0 1px inset ${COLOR['primary']}`;
export const fieldErrorShadow = `0 0 0 1px inset ${COLOR['red-dark']}`;
export const badgeSize = 12;
export const tooltipPointerHeight = 14;
export const titleBarHeight = SIZE['24'];
export const subtitleBarHeight = SIZE['16'];
export const titleGap = SIZE['8'];
export const CLICK_DELAY = 200;

export const Theme = {
  component: {
    desktopTopNavHeight: 48,
    desktopNavWidth: 80,
    desktopTopNavHeightTransform: -48,
    desktopNavWidthTransform: -70,
    histogramContainerHeight: 249,
    eventFilterHeight: 90,
    eventGridItemWidth: 320,
    eventGridItemImgHeight: 180,
    eventListPanelWidth: 380,
    eventPageLeftMargin: 450,
    camerasPageTopPanelHeight: 48,
    camerasPageSecondTitlePanelHeight: 60,
    camerasPageSidePanelWidth: 320,
    camerasPageSidePanelWidthFloorplan: 345,
    camerasPageSidePanelBorder: 2,
    videoTimePickerWidth: 700,
    videoTitleBarHeight: 48,
    videoControlHeight: 190,
    videoPlayerOptionsHeight: 48,
    cameraSelectorSearchHeight: 65,
    cameraSelectorPlayList: 160,
    cameraSelectorMiniHeight: 180,
    cameraSelectorMiniHeightMedium: 156,
    cameraSelectorMiniHeightSmall: 132,
    eventPlayerControlHeight: 40,
    miniCameraPanel: 300,
    miniCameraItemWidth: 260,
    miniCameraItemMarginRight: 5,
    miniCameraItemHeight: 160,
    miniCameraItemWidthMedium: 170,
    miniCameraItemHeightMedium: 136,
    miniCameraItemWidthSmall: 130,
    miniCameraItemHeightSmall: 112,
    bannerHeight: 50,
    eventDetailListNavButtonHeight: 50,
    previewImageWidth: 300,
    previewImageHeight: 230,
    sliderMinimizedWidth: 25,
  },
  colors: {
    green0: '#d4ebd0',
    green1: '#3bca96',
    green2: '#26a69a',
    green3: '#24ce88',
    green4: '#72bf63',
    green5: 'rgba(114, 191, 99, 0.8)',
    green6: 'rgba(59, 202, 150, 0.87)',
    green7: '#7ac142',
    green8: '#66ab59',
    green9: 'rgba(114, 191, 99, 0.3)',
    green10: 'rgba(114, 191, 99, 0.2)',
    blue1: '#088482',
    blue2: '#2580BC', //5399c8
    blue3: '#2980BB',
    blue4: 'rgba(143, 215, 251, 0.3)',
    blue5: 'rgba(0, 136, 204, 0.8)',
    blue6: 'rgba(66, 154, 213, 0.8)',
    blue7: 'rgba(37, 128, 188, 0.9)',
    blue8: 'rgba(37, 128, 188, 0.5)',
    blue9: 'rgba(37, 128, 188, 0.15)',
    blue10: 'rgba(0, 136, 204, 0.5)',
    blue11: '#c5ebff',
    blue12: '#088482',
    blue13: 'rgba(8, 132, 130, 0.5)',
    orange1: '#ffa000',
    orange2: '#ffc768',
    orange3: 'rgba(255, 160, 0, 0.8)',
    orange4: 'rgba(255, 179, 51, 0.8)',
    orange5: 'rgba(255, 179, 51, 0.5)',
    orange6: '#ffc562',
    red1: '#f54',
    red2: 'rgba(188, 0, 2, 0.87)',
    red3: 'rgba(255,85,68, 0.2)',
    dark1: 'rgba(0,0,0,0.8)',
    dark2: '#333',
    dark3: '#555',
    white: '#fff',
    // color codes used directly in css in js
    // name generated by http://chir.ag/projects/name-that-color
    blueWhale: '#042741',
    catskillWhite: '#EEF2F6',
    horizon: '#6089A8',
  },
};

export type ColorMode = 'light' | 'dark' | 'darker';
